import { GuidepostDto, DocumentationDto } from "Api/Api";
import { GuidepostCategories } from "Components/Guidepost/GuidepostCategories";
import * as React from "react";
import styled from "styled-components";
import { Resources, useResource } from "Translations/Resources";
import { DefaultButton } from "Components/Shared/Buttons/DefaultButton";
import { StyledFlex } from "Components/Shared/StyledComponents";
import { LogoutIcon } from "Components/Shared/Icons/LogoutIcon";
import {
  StyledName,
  StyledExternalApplicationLink,
} from "Components/Guidepost/GuidepostApplication";
import { InfoIcon } from "Components/Shared/Icons/InfoIcon";
import { isEmptyDocumentation } from "Components/Guidepost/GuidePostPanel";
import { GuidepostLoggedUserInfo } from "Components/Guidepost/GuidepostLoggedUserInfo";

type Props = {
  guidepost: GuidepostDto;
  documentation: DocumentationDto | null;
  setDetailOpen: (val: boolean) => void;
  handleLogout?: () => void;
};

const StyledWrapper = styled.div`
  overflow: auto;
  position: fixed;
  left: 0;
  top: 0;
  bottom: 0;
  min-width: 320px;
  background-color: ${props => props.theme.colors.white};
  box-shadow: ${props => props.theme.shadow.primary};
  z-index: 1;
  display: grid;
  grid-template-columns: 1fr auto;
  max-width: 95vw;

  h1 {
    line-height: 1;
  }
`;

const StyledDropdownContent = styled.div`
  padding-bottom: ${props => props.theme.spacing(1)}px;
  color: ${props => props.theme.palette.primary.dark};

  .ibis-MuiButtonBase-root {
    display: flex;
    justify-content: flex-start;
    padding: ${props => props.theme.spacing(0.75, 1, 0.75, 2)};
    text-transform: none;
    font-weight: 400;
    color: ${props => props.theme.palette.primary.dark};

    &:hover {
      background-color: ${props => props.theme.palette.secondary.light};
    }

    svg {
      display: block;
    }
  }

  .ibis-guidepost-app {
    padding-left: ${props => props.theme.spacing(2)}px;
  }
`;

const StyledDropdownIcon = styled.div`
  svg {
    width: 20px;
    height: 20px;
    max-height: 34px;
    display: block;
  }
`;

export const GuidepostDetail: React.FunctionComponent<Props> = props => {
  const { guidepost, documentation, setDetailOpen, handleLogout } = props;
  const { t } = useResource();
  const wrapperRef = React.useRef(null);
  const [dropdownOpen, setDropdownOpen] = React.useState<boolean>(false);

  const handleClickOutside = (e: MouseEvent) => {
    const el = wrapperRef.current as HTMLElement | null;

    if (!el?.contains(e.target as Node)) {
      setDetailOpen(false);
    }
  };

  React.useEffect(() => {
    document.removeEventListener("click", handleClickOutside);
    document.addEventListener("click", handleClickOutside);
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
    // eslint-disable-next-line
  }, [wrapperRef]);

  return (
    <StyledWrapper ref={wrapperRef}>
      <div>
        <GuidepostLoggedUserInfo
          dropdownOpen={dropdownOpen}
          setDropdownOpen={setDropdownOpen}
          displayDropdownIcon={
            (documentation && !isEmptyDocumentation(documentation)) ||
            handleLogout !== undefined
          }
        />

        {dropdownOpen && (
          <StyledDropdownContent>
            {documentation && !isEmptyDocumentation(documentation) && (
              <StyledExternalApplicationLink
                href={documentation.documentationUrl}
                target="_blank"
                rel="noopener noreferrer"
                className={"ibis-guidepost-app"}
              >
                <StyledFlex alignItems={"center"}>
                  <StyledDropdownIcon>
                    <InfoIcon width="30" height="30" />
                  </StyledDropdownIcon>
                  <StyledName>{documentation.title}</StyledName>
                </StyledFlex>
              </StyledExternalApplicationLink>
            )}
            {handleLogout && (
              <DefaultButton onClick={handleLogout}>
                <StyledFlex alignItems={"center"}>
                  <StyledDropdownIcon>
                    <LogoutIcon />
                  </StyledDropdownIcon>
                  <StyledName>{t(Resources.Auth.Logout)}</StyledName>
                </StyledFlex>
              </DefaultButton>
            )}
          </StyledDropdownContent>
        )}

        {/* <GuidepostRecentlyVisited applications={guidepost.recentApplications} /> */}

        <GuidepostCategories guidepost={guidepost} />
      </div>
    </StyledWrapper>
  );
};
